import {GlowFilter} from "@pixi/filter-glow";
import BetMenuComponent from "slotworks/components/betbar/bet-menu-component";
import BigWinComponent from "slotworks/components/bigwin/big-win-component";
import ReelSpinner from "slotworks/components/matrix/reel/transition-behaviours/spin/reel-spinner";
import PrecannedResultSelectComponent from "slotworks/components/precanned/precanned-result-select-component";
import CanvasManager from "slotworks/graphics/canvas/canvas-manager";
import Layers from "slotworks/graphics/layers/layers";
import {MultiStyleText} from "slotworks/graphics/pixi/pixi-multistyle-text";
import CampaignsModel from "slotworks/integration/revolver/model/campaigns-model";
import RevolverPrecannedResults from "slotworks/integration/revolver/precanned/revolver-precanned-results";
import BigWinTransitions from "slotworks/integration/revolver/transitions/big-win-transitions";
import LineWinTransitions from "slotworks/integration/revolver/transitions/line-win-transitions";
import MegaWinTransitions from "slotworks/integration/revolver/transitions/mega-win-transitions";
import SuperMegaWinTransitions from "slotworks/integration/revolver/transitions/super-mega-win-transitions";
import EpicWinTransitions from "slotworks/integration/revolver/transitions/epic-win-transitions";
import TotalWinTransitions from "slotworks/integration/revolver/transitions/total-win-transitions";
import SettingsModel, { Jurisdiction } from "slotworks/model/settings/settings-model";
import OfflinePrecannedResults from "slotworks/server/precanned/offline-precanned-results";
import DisabledSpinButtonBehaviour from "slotworks/smart-button/behaviours/disabled-spin-button-behaviour";
import FreespinButtonBehaviour from "slotworks/smart-button/behaviours/freespin-button-behaviour";
import HiddenButtonBehaviour from "slotworks/smart-button/behaviours/hidden-button-behaviour";
import PromoFreespinDisabledButtonBehaviour from "slotworks/smart-button/behaviours/promo-fs-disabled-button-behaviour";
import PromoFreespinStartButtonBehaviour from "slotworks/smart-button/behaviours/promo-fs-start-button-behaviour";
import PromoFreespinStopButtonBehaviour from "slotworks/smart-button/behaviours/promo-fs-stop-button-behaviour";
import SelectAutoButtonBehaviour from "slotworks/smart-button/behaviours/select-auto-button-behaviour";
import SkipButtonBehaviour from "slotworks/smart-button/behaviours/skip-button-behaviour";
import SpinButtonBehaviour from "slotworks/smart-button/behaviours/spin-button-behaviour";
import SmartButton, {SmartButtonFlag} from "slotworks/smart-button/smart-button";
import Autoplay from "slotworks/state-machine/standard/states/autoplay";
import FreespinIdle from "slotworks/state-machine/standard/states/freespin-idle";
import ShowWinLines from "slotworks/state-machine/standard/states/show-win-lines";
import DeviceInfo from "slotworks/utils/device-info";
import WrapperMediator from "slotworks/integration/revolver/wrapper/wrapper-mediator";
import GameLoop from "slotworks/core/game-loop";
import Components from "slotworks/components/components";
import canvasManager from "slotworks/graphics/canvas/canvas-manager";
import Regulation from "slotworks/integration/revolver/regulations/regulation";
import SlotDefinition from "slotworks/model/slot-definition";

declare const __OFFLINE__: boolean;
declare const __DEBUG__: boolean;
declare const __ENVIRONMENT__: string;

export function RevolverInit() {
    FreespinIdle.WAIT = 500;
    Autoplay.WAIT = 500;

    Layers.Menus.onSceneEnter.addOnce(() => {
        const bar: PIXI.DisplayObject = Layers.Menus.getSprite("loader_bar");

        if (!DeviceInfo.isSamsungBrowser()) {
            const filter = new GlowFilter(10, 2, 2, 0xff9d40, 0.5);
            filter.padding = 20;
            bar.filters = [filter];
        }
    });
}

export function RevolverReady() {
    WrapperMediator.gameLoaded();

    ShowWinLines.MULTILINE_ONLY = true;
    ReelSpinner.minRepeats = 25;

    if ("gameWrapper" in window) {
        // @ts-ignore
        const wrapper = window.gameWrapper;

        wrapper.pauseGame = () => {
            if (GameLoop.isPaused()) {
                return;
            }

            canvasManager.exitFullScreen();

            const pointerEvents = document.body.style.pointerEvents;
            document.body.style.pointerEvents = "none";

            const hasPromptFlag = SmartButton.hasFlag(SmartButtonFlag.PROMPT);
            SmartButton.setFlag(SmartButtonFlag.PROMPT, true);

            const isBetBarEnabled = Components.betBar.isEnabled();

            GameLoop.setPaused(true);
            Components.betBar.setEnabled(false);

            wrapper.resumeGame = () => {
                canvasManager.enableFullScreen();

                if (!GameLoop.isPaused()) {
                    return;
                }

                document.body.style.pointerEvents = pointerEvents;

                GameLoop.setPaused(false);
                Components.betBar.setEnabled(isBetBarEnabled);
                SmartButton.setFlag(SmartButtonFlag.PROMPT, hasPromptFlag);
            }
        }
    }

    /// #if __DEBUG__
    if (__DEBUG__) {
        MultiStyleText.debugOptions.autoShrink.enabled = true;
    }
    /// #endif

    if (__OFFLINE__) {
        PrecannedResultSelectComponent.create(OfflinePrecannedResults);
    } else if (__ENVIRONMENT__ !== "production") {
        PrecannedResultSelectComponent.create(RevolverPrecannedResults);
    }

    if (DeviceInfo.layoutId === "desktop") {
        CanvasManager.setPortraitEnabled(false);

        SelectAutoButtonBehaviour.OPEN_AUTOPLAY_POPUP = true;
        FreespinButtonBehaviour.HIDDEN = true;

        for (let mask = 0; mask < SmartButtonFlag.MAX; mask++) {
            // Revolver does not have a stop auto spin button during freespins
            if ((mask & SmartButtonFlag.FREE_SPINS) > 0) {
                SmartButton.overrideBehaviour(mask, FreespinButtonBehaviour);
            }
            // Buttons hide during bonuses in free spins
            if ((mask & SmartButtonFlag.FREE_SPINS) > 0 && (mask & SmartButtonFlag.BONUS) > 0) {
                SmartButton.overrideBehaviour(mask, HiddenButtonBehaviour);
            }
            if ((mask & SmartButtonFlag.PROMPT) > 0) {
                if ((mask & SmartButtonFlag.FREE_SPINS) > 0) {
                    SmartButton.overrideBehaviour(mask, HiddenButtonBehaviour);
                } else {
                    SmartButton.overrideBehaviour(mask, DisabledSpinButtonBehaviour);
                }
            }
        }

        if ("gameWrapper" in window) {
            BetMenuComponent.sceneAliases.set(BetMenuComponent.SETTINGS, "settings"); 
        } else if (SettingsModel.isJurisdiction([Jurisdiction.UK, Jurisdiction.NL])) {
            if(!SettingsModel.rcdisabled) {
                BetMenuComponent.sceneAliases.set(BetMenuComponent.SETTINGS, "settings_uk");
            }
        }

    } else if (DeviceInfo.layoutId === "mobile") {
        for (let mask = 0; mask < SmartButtonFlag.MAX; mask++) {
            if ((mask & SmartButtonFlag.FREE_SPINS) > 0) {
                SmartButton.overrideBehaviour(mask, FreespinButtonBehaviour);
            }
            if ((mask & SmartButtonFlag.BONUS) > 0) {
                SmartButton.overrideBehaviour(mask, HiddenButtonBehaviour);
            }
            if ((mask & SmartButtonFlag.PROMPT) > 0) {
                SmartButton.overrideBehaviour(mask, HiddenButtonBehaviour);
            }
        }
    }
    
    if(CampaignsModel.isFreespinsActive())
    {
        for (let mask = 0; mask < SmartButtonFlag.MAX; mask++) {
            if((mask & SmartButtonFlag.FREE_SPINS) > 0)
            {
                SmartButton.overrideBehaviour(mask, PromoFreespinStartButtonBehaviour);
            }
            if((mask & SmartButtonFlag.AUTO_SPIN) > 0)
            {
                SmartButton.overrideBehaviour(mask, PromoFreespinStopButtonBehaviour);
            }
            if((mask & SmartButtonFlag.AWAITING_RESPONSE) > 0)
            {
                SmartButton.overrideBehaviour(mask, PromoFreespinDisabledButtonBehaviour);
            }
        }
    }

    CanvasManager.stage.addChild(Layers.BetBar.container);
    CanvasManager.stage.addChild(Layers.SmartButton.container);
    
    Regulation.setup();

    if(SlotDefinition.isSingleBetLevel()) {
        document.documentElement.classList.toggle("single_bet_hidden", true);
    }
}

export function RevolverStandardWinTransitions() {
    BigWinComponent.CLICK_TO_SKIP = true;
    BigWinComponent.HANG_TIME = 3000;

    const transitions = [new TotalWinTransitions(), new BigWinTransitions(), new MegaWinTransitions(), new SuperMegaWinTransitions(),new EpicWinTransitions];

    for (let row = 1; row <= 4; row++) {
        if (Layers.CelebrationContent.hasScene(`line_row_${row}_win`)) {
            Layers.CelebrationContent.setTransitions(`line_row_${row}_win`, new LineWinTransitions());
        }
    }
    if (Layers.CelebrationContent.hasScene("lineWin")) {
        Layers.CelebrationContent.setTransitions("lineWin", new LineWinTransitions());
    }
    Layers.CelebrationContent.setTransitions("totalWin", transitions[0]);
    Layers.CelebrationContent.setTransitions("bigWin", transitions[1]);
    Layers.CelebrationContent.setTransitions("megaWin", transitions[2]);
    Layers.CelebrationContent.setTransitions("superMegaWin", transitions[3]);
    Layers.CelebrationContent.setTransitions("epicWin", transitions[4]);

    return transitions;
}



// WEBPACK FOOTER //
// ./~/slotworks/integration/revolver/revolver-setup.ts